/**
 * Vertical board layout — stacked chrome in grid units U (= side-column width).
 * Play: 4U × 4.5U (same as horizontal). Stack: 0.5U header + 4.5U play + 1U actions + 0.5U strip = 6.5U.
 * Mandatory scenery: 0.1U per side and 0.1U top/bottom (same size). Extra viewport
 * height beyond the minimum stack + margins: one quarter as extra top margin above
 * the stack, one quarter grows the play band (gameplay bottom-aligned), and half
 * grows the evolution-strip band (header and stash stay fixed).
 */
import {
  COLUMN_UNITS_TOTAL,
  DEFAULT_BOTTOM_BAR_HEIGHT_PX,
  GRID_HEIGHT_SIDE_MULTIPLIER,
  MIN_GRID_UNIT_PX,
  MOCKUP_PLAY_AREA_RATIO,
} from "./responsive-board.js";

/** Grid unit U = side-column width (matches horizontal `gridUnitPx`). */
export const GRID_UNIT = 1;

/** Play column width in U (matches horizontal). */
export const PLAY_WIDTH_UNITS = 4;

/** Play column height in U (matches horizontal `GRID_HEIGHT_SIDE_MULTIPLIER`). */
export const PLAY_HEIGHT_UNITS = GRID_HEIGHT_SIDE_MULTIPLIER;

/** Header band height in U. */
export const HEADER_BAND_UNITS = 0.5;

/** Stash / actions band height in U. */
export const STASH_BAND_UNITS = 1;

/** Evolution strip band height in U. */
export const EVOLUTION_STRIP_UNITS = 0.5;

/** Total stacked chrome + play height in U. */
export const VERTICAL_STACK_UNITS =
  HEADER_BAND_UNITS + PLAY_HEIGHT_UNITS + STASH_BAND_UNITS + EVOLUTION_STRIP_UNITS;

/** Minimum horizontal scenery (each side) in U. */
export const SIDE_MARGIN_UNITS = 0.1;

/** Minimum content width: play + side margins (4U + 0.1U + 0.1U). */
export const CONTENT_WIDTH_UNITS = PLAY_WIDTH_UNITS + SIDE_MARGIN_UNITS * 2;

/** Minimum content height: stack + top/bottom margins (6.5U + 0.1U + 0.1U). */
export const CONTENT_HEIGHT_UNITS = VERTICAL_STACK_UNITS + SIDE_MARGIN_UNITS * 2;

/** Content inset inside play bounds on each horizontal side (gameplay / overlays). */
export const PLAY_SIDE_INSET_FRACTION = 0.05;

/** @deprecated Use {@link HEADER_BAND_UNITS} / {@link VERTICAL_STACK_UNITS}. */
export const HEADER_BAND_FRACTION = HEADER_BAND_UNITS / VERTICAL_STACK_UNITS;

/** @deprecated Use {@link STASH_BAND_UNITS} / {@link VERTICAL_STACK_UNITS}. */
export const STASH_BAND_FRACTION = STASH_BAND_UNITS / VERTICAL_STACK_UNITS;

/** @deprecated Use {@link EVOLUTION_STRIP_UNITS} / {@link VERTICAL_STACK_UNITS}. */
export const EVOLUTION_STRIP_FRACTION = EVOLUTION_STRIP_UNITS / VERTICAL_STACK_UNITS;

/** @deprecated Use {@link PLAY_HEIGHT_UNITS} / {@link VERTICAL_STACK_UNITS}. */
export const PLAY_BAND_FRACTION = PLAY_HEIGHT_UNITS / VERTICAL_STACK_UNITS;

function getMinSideMarginPx(unit) {
  return Math.max(0, Math.floor(unit * SIDE_MARGIN_UNITS));
}

/**
 * @param {number} unit grid unit U in px (even integer)
 * @returns {{ headerH: number, playH: number, stashH: number, stripH: number, stackH: number }}
 */
export function stackHeightsPx(unit) {
  var headerH = Math.floor(unit * HEADER_BAND_UNITS);
  var playH = Math.floor(unit * PLAY_HEIGHT_UNITS);
  var stashH = unit * STASH_BAND_UNITS;
  var stripH = Math.floor(unit * EVOLUTION_STRIP_UNITS);
  return {
    headerH: headerH,
    playH: playH,
    stashH: stashH,
    stripH: stripH,
    stackH: headerH + playH + stashH + stripH,
  };
}

function verticalStackFits(unit, usableH, boardW) {
  var bands = stackHeightsPx(unit);
  var playW = unit * PLAY_WIDTH_UNITS;
  var minMargin = getMinSideMarginPx(unit);
  var minHoriz = minMargin * 2;
  var minVert = minMargin * 2;
  return usableH >= bands.stackH + minVert && boardW >= playW + minHoriz;
}

function resolvePlaySideInsetPx(playW) {
  return Math.max(0, Math.floor(playW * PLAY_SIDE_INSET_FRACTION));
}

function resolveHorizontalScenery(boardW, playW, unit) {
  var minSide = getMinSideMarginPx(unit);
  var minContentW = playW + minSide * 2;
  var extraHoriz = Math.max(0, boardW - minContentW);
  var sceneryLeft = minSide + Math.floor(extraHoriz / 2);
  var sceneryRight = minSide + (extraHoriz - Math.floor(extraHoriz / 2));
  return { left: sceneryLeft, right: sceneryRight };
}

function resolveVerticalBandGrowth(usableH, minStackH, unit) {
  var minSide = getMinSideMarginPx(unit);
  var minTotalH = minStackH + minSide * 2;
  var extraVert = Math.max(0, usableH - minTotalH);
  var topExtra = Math.floor(extraVert / 4);
  var playExtra = Math.floor(extraVert / 4);
  var stripExtra = extraVert - topExtra - playExtra;
  return {
    sceneryTop: minSide + topExtra,
    sceneryBottom: minSide,
    topMarginExtra: topExtra,
    playExtra: playExtra,
    stripExtra: stripExtra,
  };
}

/**
 * @param {{ width: number, height: number }} viewport
 * @param {{ bottomBarHeightPx?: number }} [opts]
 */
export function computeVerticalBoard(viewport, opts) {
  opts = opts || {};
  var vw = Math.max(320, Math.floor(viewport.width || 0));
  var vh = Math.max(240, Math.floor(viewport.height || 0));
  var bottomBarH = opts.bottomBarHeightPx != null
    ? Math.max(0, Math.floor(Number(opts.bottomBarHeightPx)))
    : DEFAULT_BOTTOM_BAR_HEIGHT_PX;
  var boardW = vw;
  var boardH = vh;
  var bottomBarY = boardH - bottomBarH;
  var usableH = bottomBarY;

  // Vertical stack fills the viewport (no horizontal mockup breathing-room inset).
  // Extra height only letterboxes when the 6.5U stack is shorter than the board.
  var unitMaxW = Math.floor(vw / CONTENT_WIDTH_UNITS);
  var unitMaxH = Math.floor(usableH / CONTENT_HEIGHT_UNITS);
  var unit = Math.min(unitMaxW, unitMaxH);
  if (unit < MIN_GRID_UNIT_PX) {
    unit = Math.max(MIN_GRID_UNIT_PX, Math.min(unitMaxW, unitMaxH));
  }
  unit -= unit % 2;
  if (unit < 2) unit = 2;

  while (unit > MIN_GRID_UNIT_PX && !verticalStackFits(unit, usableH, boardW)) {
    unit -= 2;
  }
  if (!verticalStackFits(unit, usableH, boardW) && unit > 2) {
    unit = Math.max(2, MIN_GRID_UNIT_PX);
    unit -= unit % 2;
    if (unit < 2) unit = 2;
  }

  while (unit + 2 <= unitMaxW && verticalStackFits(unit + 2, usableH, boardW)) {
    unit += 2;
  }

  var playW = unit * PLAY_WIDTH_UNITS;
  var bands = stackHeightsPx(unit);
  var growth = resolveVerticalBandGrowth(usableH, bands.stackH, unit);
  var sceneryTop = growth.sceneryTop;
  var sceneryBottom = growth.sceneryBottom;
  var headerH = bands.headerH;
  var playH = bands.playH + growth.playExtra;
  var stripH = bands.stripH + growth.stripExtra;
  var contentH = headerH + playH + bands.stashH + stripH;

  var horiz = resolveHorizontalScenery(boardW, playW, unit);
  var sceneryLeft = horiz.left;
  var sceneryRight = horiz.right;
  var coreX = sceneryLeft;
  var contentY = sceneryTop;

  var headerY = contentY;
  var playY = headerY + headerH;
  var stashY = playY + playH;
  var stripY = stashY + bands.stashH;

  var playInset = resolvePlaySideInsetPx(playW);
  var playContentW = Math.max(0, playW - playInset * 2);

  return {
    boardWidth: boardW,
    boardHeight: boardH,
    sceneryMargins: {
      left: sceneryLeft,
      right: sceneryRight,
      top: sceneryTop,
      bottom: sceneryBottom,
    },
    coreRect: {
      x: coreX,
      y: contentY,
      width: playW,
      height: contentH,
    },
    playArea: {
      x: coreX,
      y: playY,
      width: playW,
      height: playH,
    },
    playContentArea: {
      x: coreX + playInset,
      y: playY + growth.playExtra,
      width: playContentW,
      height: bands.playH,
    },
    headerBand: {
      x: 0,
      y: headerY,
      width: boardW,
      height: headerH,
    },
    stashBand: {
      x: 0,
      y: stashY,
      width: boardW,
      height: bands.stashH,
    },
    evolutionStripBand: {
      x: 0,
      y: stripY,
      width: boardW,
      height: stripH,
    },
    bottomBar: {
      x: 0,
      y: bottomBarY,
      width: boardW,
      height: bottomBarH,
    },
    evolutionDockBounds: null,
    bottomBarHeightPx: bottomBarH,
    playAreaRatio: playW / bands.playH,
    coreRatio: playW / contentH,
    gridUnitPx: unit,
    gridWidthPx: unit * COLUMN_UNITS_TOTAL,
    playSideInsetFraction: PLAY_SIDE_INSET_FRACTION,
    playSideInsetPx: playInset,
    playBandTopExtraPx: growth.playExtra,
    topMarginExtraPx: growth.topMarginExtra,
    sideMarginPx: getMinSideMarginPx(unit),
    bandUnits: {
      header: HEADER_BAND_UNITS,
      play: PLAY_HEIGHT_UNITS,
      stash: STASH_BAND_UNITS,
      evolutionStrip: EVOLUTION_STRIP_UNITS,
      stack: VERTICAL_STACK_UNITS,
      sideMargin: SIDE_MARGIN_UNITS,
      contentWidth: CONTENT_WIDTH_UNITS,
      contentHeight: CONTENT_HEIGHT_UNITS,
    },
    bandFractions: {
      header: HEADER_BAND_UNITS / VERTICAL_STACK_UNITS,
      play: PLAY_HEIGHT_UNITS / VERTICAL_STACK_UNITS,
      stash: STASH_BAND_UNITS / VERTICAL_STACK_UNITS,
      evolutionStrip: EVOLUTION_STRIP_UNITS / VERTICAL_STACK_UNITS,
    },
  };
}

/**
 * Assert vertical layout relationships for tests.
 * @param {ReturnType<typeof computeVerticalBoard>} board
 */
export function validateVerticalBoard(board) {
  var play = board.playArea;
  var core = board.coreRect;
  var header = board.headerBand;
  var stash = board.stashBand;
  var strip = board.evolutionStripBand;
  var unit = board.gridUnitPx;
  var bands = stackHeightsPx(unit);
  var content = board.playContentArea;

  if (play.width !== unit * PLAY_WIDTH_UNITS) {
    throw new Error("play width not 4U: " + play.width + " vs " + unit * PLAY_WIDTH_UNITS);
  }
  if (play.height < bands.playH - 1) {
    throw new Error("play height below 4.5U min: " + play.height + " vs " + bands.playH);
  }
  if (header.height < bands.headerH - 1 || header.height > bands.headerH + 1) {
    throw new Error("header band must stay at 0.5U: " + header.height + " vs " + bands.headerH);
  }
  if (stash.height !== bands.stashH) {
    throw new Error("stash band not 1U: " + stash.height + " vs " + bands.stashH);
  }
  if (strip.height < bands.stripH - 1) {
    throw new Error("strip band below 0.5U min: " + strip.height + " vs " + bands.stripH);
  }

  if (Math.abs(play.width / content.height - MOCKUP_PLAY_AREA_RATIO) > 0.02) {
    throw new Error("play content ratio off: " + (play.width / content.height));
  }

  if (content.height !== bands.playH) {
    throw new Error("play content height must stay 4.5U: " + content.height + " vs " + bands.playH);
  }
  if (content.y !== play.y + play.height - bands.playH) {
    throw new Error(
      "play content must stay bottom-aligned in play band: y=" + content.y +
      " expected=" + (play.y + play.height - bands.playH),
    );
  }

  var minSide = getMinSideMarginPx(unit);
  var playGrowth = play.height - bands.playH;
  var stripGrowth = strip.height - bands.stripH;
  var topMarginExtra = board.sceneryMargins.top - minSide;
  if (Math.abs(playGrowth - topMarginExtra) > 1) {
    throw new Error(
      "play growth must match top margin extra: play+" + playGrowth + " top+" + topMarginExtra,
    );
  }
  if (stripGrowth < playGrowth - 1) {
    throw new Error(
      "strip growth must exceed play growth: play+" + playGrowth + " strip+" + stripGrowth,
    );
  }

  var budget =
    header.height +
    play.height +
    stash.height +
    strip.height;
  if (core.height !== budget) {
    throw new Error("core height must match stacked bands: " + core.height + " vs " + budget);
  }
  if (core.width !== play.width) {
    throw new Error("core width must match play width");
  }

  if (header.y !== core.y) {
    throw new Error("header must start at core top");
  }
  if (play.y !== header.y + header.height) {
    throw new Error("play must follow header");
  }
  if (stash.y !== play.y + play.height) {
    throw new Error("stash must follow play");
  }
  if (strip.y !== stash.y + stash.height) {
    throw new Error("strip must follow stash");
  }

  if (board.sceneryMargins.top < minSide - 1 || board.sceneryMargins.bottom < minSide - 1) {
    throw new Error(
      "vertical scenery below 0.1U: top=" + board.sceneryMargins.top +
      " bottom=" + board.sceneryMargins.bottom + " min=" + minSide,
    );
  }

  if (board.sceneryMargins.left < minSide - 1 || board.sceneryMargins.right < minSide - 1) {
    throw new Error(
      "horizontal scenery below 0.1U: left=" + board.sceneryMargins.left +
      " right=" + board.sceneryMargins.right + " min=" + minSide,
    );
  }
  if (Math.abs(
    (board.sceneryMargins.left - minSide) - (board.sceneryMargins.right - minSide),
  ) > 1) {
    throw new Error("extra horizontal scenery not evenly split");
  }

  var inset = board.playSideInsetPx;
  if (content.x !== play.x + inset) {
    throw new Error("play content x mismatch");
  }
  if (content.width !== play.width - inset * 2) {
    throw new Error("play content width mismatch");
  }
  if (Math.abs(inset - Math.floor(play.width * PLAY_SIDE_INSET_FRACTION)) > 1) {
    throw new Error("play side inset off");
  }

  var used =
    board.sceneryMargins.top +
    budget +
    board.sceneryMargins.bottom +
    board.bottomBar.height;
  if (used !== board.boardHeight) {
    throw new Error("vertical stack does not fill board height: " + used + " vs " + board.boardHeight);
  }
}